/**
 * WaitingCustomersWidget — 07/2026
 *
 * Dashboard-Widget: Kunden die auf eine Antwort warten.
 * Kombiniert offene KI-Eskalationen + übernommene WhatsApp-Gespräche.
 * Versteckt sich automatisch, wenn niemand wartet.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MessageCircleWarning, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeAgo = (iso) => {
  if (!iso) return '';
  const min = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} Min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `vor ${h} Std`;
  return `vor ${Math.floor(h / 24)} Tagen`;
};

const WaitingCustomersWidget = () => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/dashboard/waiting-customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      // Stiller Fehler — Widget bleibt verborgen
    }
  }, []);

  useEffect(() => {
    fetchData();
    const refresh = setInterval(fetchData, 60000);
    return () => clearInterval(refresh);
  }, [fetchData]);

  if (!data || data.total_waiting === 0) return null;

  const waitingTakeovers = (data.takeovers || []).filter(t => t.customer_waiting);

  return (
    <Card className="border-2 border-orange-300 bg-orange-50" data-testid="waiting-customers-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base text-orange-900">
          <MessageCircleWarning size={20} className="text-orange-600" />
          {data.total_waiting} {data.total_waiting === 1 ? 'Kunde wartet' : 'Kunden warten'} auf Antwort
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {(data.escalations || []).map((esc) => (
          <div
            key={esc.id}
            className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-orange-100"
            data-testid={`waiting-escalation-${esc.id}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900">{esc.customer_name || esc.customer_phone}</span>
                <Badge variant="outline" className="h-5 text-[10px] px-1.5 border-red-300 text-red-700">
                  KI-Eskalation
                </Badge>
                <span className="text-xs text-gray-400">{timeAgo(esc.created_at)}</span>
              </div>
              <p className="text-xs text-gray-600 truncate">❓ {esc.customer_question}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 h-7 text-xs border-orange-300"
              onClick={() => navigate('/settings/advanced?tab=ki-api&subtab=chatbot')}
              data-testid={`waiting-escalation-open-${esc.id}`}
            >
              Beantworten <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
        ))}
        {waitingTakeovers.map((t) => (
          <div
            key={t.chat_id}
            className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2 border border-orange-100"
            data-testid={`waiting-takeover-${t.chat_id}`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-gray-900">{t.customer_name}</span>
                <Badge variant="outline" className="h-5 text-[10px] px-1.5 border-amber-300 text-amber-700">
                  🤝 Übernommen{t.admin_name ? ` von ${t.admin_name}` : ''}
                </Badge>
                <span className="text-xs text-gray-400">{timeAgo(t.last_message_at)}</span>
              </div>
              <p className="text-xs text-gray-600 truncate">💬 {t.last_customer_message}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0 h-7 text-xs border-orange-300"
              onClick={() => navigate('/whatsapp')}
              data-testid={`waiting-takeover-open-${t.chat_id}`}
            >
              Zum Chat <ArrowRight size={12} className="ml-1" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default WaitingCustomersWidget;
